/**
 * The Prüvit Professional application form.
 *
 * MV Qore owns the transport as of 2026-10-02; this owns what the applicant
 * sees on the way to it. Four things:
 *
 *   1. Field-level errors. The browser's own validation bubbles cannot be
 *      styled and arrive one field at a time, so the form carries novalidate
 *      in the markup and the messages are written beside their field, into the
 *      live region each field already points at.
 *
 *   2. The website alternative. "I don't have a public business page" swaps
 *      which of two fields is required: the website while it is unticked, the
 *      verification field it reveals while it is ticked.
 *
 *   3. The captcha. Bot protection is on for this store, so a submission with
 *      no frc-captcha-response is refused server-side. MV Qore's own block
 *      used to carry the widget and is block-scoped, so the theme mounts its
 *      own — the same arrangement, and the same loader, as the lead form.
 *
 *   4. Handing over to the SDK, which differs by who is asking:
 *        signed out  attachRegistrationForm    creates the customer, carries
 *                                              identity AND the answers
 *        signed in   attachCustomerFieldsForm  carries only the answers and
 *                                              updates the existing customer
 *      The mode comes from the markup, which resolved `customer` in Liquid,
 *      rather than being guessed from which fields happen to be present.
 */
(() => {
  const roots = document.querySelectorAll('[data-pruvit-apply]');
  if (!roots.length) return;

  /**
   * v2 of the widget, which writes `frc-captcha-response` — the field the SDK
   * reads. v1 wrote `frc-captcha-solution` and needed the theme to copy it
   * across; see the long note in pruvit-lead.js for why that is gone.
   */
  const FRIENDLY_CAPTCHA_SRC = 'https://cdn.jsdelivr.net/npm/@friendlycaptcha/sdk@1.1.1/site.min.js';

  /** The tag Flow watches. Rejected unless MV Qore's allowlist carries it. */
  const SUBMIT_TAG = 'mvqore-application-submitted';

  /** One Friendly Captcha script for the page however many forms want it. */
  let captchaScript = null;
  const loadCaptcha = () => {
    if (captchaScript) return captchaScript;
    captchaScript = new Promise((resolve) => {
      const s = document.createElement('script');
      s.type = 'module';
      s.src = FRIENDLY_CAPTCHA_SRC;
      s.async = true;
      s.onload = () => resolve(true);
      s.onerror = () => {
        console.warn('[pruvit-apply] Friendly Captcha did not load from ' + FRIENDLY_CAPTCHA_SRC);
        resolve(false);
      };
      document.head.appendChild(s);
    });
    return captchaScript;
  };

  const readStrings = (root) => {
    const tag = root.querySelector('[data-pruvit-apply-strings]');
    try {
      return tag ? JSON.parse(tag.textContent) : {};
    } catch (e) {
      return {};
    }
  };

  /**
   * The `t` filter HTML-escapes what it interpolates and these are written
   * with textContent, which does not decode — so an apostrophe would arrive
   * as &#39; on screen. Same trap, same fix, as the lead form.
   */
  const decodeEntities = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const box = document.createElement('textarea');
    box.innerHTML = value;
    return box.value;
  };

  const setup = async (root) => {
    const form = root.querySelector('[data-pruvit-apply-form]');
    if (!form) return;

    const strings = readStrings(root);
    const say = (key, fallback) => decodeEntities(strings[key] || fallback);
    // The two "already has an account" messages go to innerHTML, which decodes
    // for itself. Decoding first turned the sign-in link's escaped
    // `&amp;region_country` into `&region_country`, which the parser reads as
    // the entity `&reg` -- the link arrived as ®ion_country and dropped the
    // shopper's country. Found on the survey page 2026-10-09, same pipeline.
    const sayHtml = (key, fallback) => strings[key] || fallback;

    const mode = form.dataset.pruvitApplyMode === 'fields' ? 'fields' : 'register';
    // Set once bot protection has answered; read by resetCaptcha, which is
    // defined before that await and only ever CALLED after it.
    let botEnabled = false;
    const submitBtn = root.querySelector('[data-pruvit-apply-submit]');
    const site = root.querySelector('[data-pruvit-apply-site]');
    const noSite = root.querySelector('[data-pruvit-apply-nosite]');
    const alt = root.querySelector('[data-pruvit-apply-alt]');
    const altInput = alt ? alt.querySelector('input') : null;
    const captchaBox = root.querySelector('[data-pruvit-apply-captcha]');
    const formError = root.querySelector('[data-pruvit-apply-error]');
    const done = root.querySelector('[data-pruvit-apply-done]');

    // The error line is the last id in aria-describedby, by construction in
    // the block: hints come first, the error last.
    const errorFor = (field) => {
      const ids = (field.getAttribute('aria-describedby') || '').trim().split(/\s+/);
      const id = ids[ids.length - 1];
      return id ? document.getElementById(id) : null;
    };

    const showError = (field, message, asHtml) => {
      const line = errorFor(field);
      field.setAttribute('aria-invalid', 'true');
      if (!line) return;
      // Only the "already has an account" message carries markup, and it is
      // built by the `t` filter in the block rather than from anything the
      // applicant typed.
      if (asHtml) line.innerHTML = message;
      else line.textContent = message;
      line.hidden = false;
    };

    const clearError = (field) => {
      const line = errorFor(field);
      field.removeAttribute('aria-invalid');
      if (line) {
        line.textContent = '';
        line.hidden = true;
      }
    };

    const showFormError = (message) => {
      if (!formError) return;
      formError.textContent = message;
      formError.hidden = false;
    };
    const clearFormError = () => {
      if (!formError) return;
      formError.textContent = '';
      formError.hidden = true;
    };

    /**
     * Throw the used answer away after a rejected submission.
     *
     * QA, 2026-10-02: hit "email already in use", correct it, submit again,
     * and the form says "finishing the security check" and does nothing. A
     * Friendly Captcha solution is SINGLE USE — once the server has presented
     * one to siteverify it is spent, so the second submit carried a token that
     * was refused for a reason with nothing to do with the email. The visitor
     * fixes the real problem and is told no again.
     *
     * The lead form already hit this on Create Account and already solved it;
     * this is that solution, not a second one. Reset on EVERY error, not just
     * the captcha-shaped ones: whether the server spends the token before or
     * after it validates the email is its business, not something the theme
     * should model, and a fresh answer is never the wrong thing to hold.
     *
     * data-start="auto" is what makes the re-solve automatic; without it this
     * would leave the widget waiting for a click nobody knows to give.
     */
    const resetCaptcha = () => {
      if (!botEnabled || !captchaBox) return;
      try {
        const api = window.frcaptcha;
        if (!api || typeof api.getAllWidgets !== 'function') return;
        const widget = api
          .getAllWidgets()
          .find((w) => w.getElement && (w.getElement() === captchaBox || captchaBox.contains(w.getElement())));
        if (widget && typeof widget.reset === 'function') widget.reset();
      } catch (e) {
        // Never at the expense of the message. Somebody who cannot be told
        // what went wrong is worse off than one carrying a stale token.
        console.warn('[pruvit-apply] could not reset the captcha:', e);
      }
    };

    const messageFor = (field) => {
      if (field.validity.valueMissing) {
        if (field.name === 'terms_accepted') return say('errTerms', 'Please accept the terms to continue.');
        if (field.type === 'checkbox') return say('errAck', 'Please tick this box to continue.');
        if (field.tagName === 'SELECT') return say('errSelect', 'Please choose an option.');
        return say('errRequired', 'Please fill in this field.');
      }
      if (field.type === 'email' && field.validity.typeMismatch) {
        return say('errEmail', 'That does not look like an email address.');
      }
      return field.validationMessage || say('errRequired', 'Please fill in this field.');
    };

    // Everything named and visible. It used to be everything called
    // contact[...]; the names are now a mix of the SDK's own customer fields
    // and the mvqore_form.* answers, so the test is "has a name and is not
    // hidden" rather than a prefix.
    //
    // The referrer step's code field is in the form but is not an answer: it
    // is checked by its own button and gated by its own rule below, so it
    // stays out of this list.
    const fields = [...form.elements].filter(
      (el) => el.name && el.type !== 'hidden' && !el.closest('[data-pruvit-ref]')
    );

    // A field inside a hidden wrapper is not on screen and must not block the
    // submit — the verification field while the box is unticked.
    const isLive = (field) => !field.disabled && !field.closest('[hidden]');

    const syncSite = () => {
      const off = !!(noSite && noSite.checked);
      if (site) {
        site.required = !off;
        if (off) clearError(site);
      }
      if (alt && altInput) {
        alt.hidden = !off;
        altInput.required = off;
        if (!off) clearError(altInput);
      }
    };

    if (noSite) {
      noSite.addEventListener('change', () => {
        syncSite();
        if (noSite.checked && altInput) altInput.focus();
      });
      syncSite();
    }

    fields.forEach((field) => {
      const check = () => {
        if (field.getAttribute('aria-invalid') === 'true' && field.checkValidity()) clearError(field);
      };
      field.addEventListener('input', check);
      field.addEventListener('change', check);
    });

    const validate = () => {
      let first = null;
      fields.forEach((field) => {
        if (!isLive(field)) {
          clearError(field);
          return;
        }
        if (field.checkValidity()) {
          clearError(field);
          return;
        }
        showError(field, messageFor(field));
        if (!first) first = field;
      });
      return first;
    };

    // The referrer step, mounted once the SDK is here (below). Register mode
    // only; it is not drawn for a signed-in applicant.
    let step = null;

    // REGISTERED BEFORE THE SDK'S OWN SUBMIT HANDLER, which is what makes
    // stopImmediatePropagation work: listeners run in the order they were
    // added, so an invalid form never reaches MV Qore at all.
    form.addEventListener('submit', (event) => {
      clearFormError();
      const first = validate();
      // No referrer, no application. Craig, 2026-10-05: with Referrer Bypass
      // off, a submission that creates a customer must not go through without
      // one, and the button stays live and explains itself. The fields are
      // still all marked first, so nothing is left to discover on a second
      // press; the caret goes to the referrer because it is the first
      // question on the form.
      if (mode === 'register' && step && !step.has()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        step.require();
        return;
      }
      if (first) {
        event.preventDefault();
        event.stopImmediatePropagation();
        first.focus();
        return;
      }
      if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');
    });

    const finish = () => {
      if (submitBtn) submitBtn.removeAttribute('aria-busy');
    };

    const onSuccess = () => {
      finish();
      form.hidden = true;
      if (done) {
        done.hidden = false;
        done.focus();
      }
    };

    const onError = (error) => {
      finish();
      // Before the message, so the widget is already re-solving while they
      // read it. By the time anyone has understood the error and corrected a
      // field there is a fresh answer waiting.
      resetCaptcha();
      const code = error && error.code;
      const emailField = form.querySelector('[name="email"]');
      const phoneField = form.querySelector('[name="phone"]');

      // The duplicate paths get the message on the field that caused them,
      // with the way forward in it, rather than a generic failure at the foot
      // of the form. We never write to a record somebody merely named.
      if (code === 'EMAIL_IN_USE' && emailField) {
        showError(emailField, sayHtml('errInUse', 'That email already has an account.'), true);
        emailField.focus();
        return;
      }
      if (code === 'PHONE_IN_USE' && phoneField) {
        // Its own string: the email one says "That email", which is the wrong
        // noun on a phone field.
        showError(phoneField, sayHtml('errPhoneInUse', 'That phone number already has an account.'), true);
        phoneField.focus();
        return;
      }
      if (code === 'REFERRER_REQUIRED' && step) {
        // The SDK found no stored referrer although the step showed one --
        // cleared in another tab, say. Back to the question.
        step.require();
        return;
      }
      if (code === 'REGISTRATION_TOO_FAST' || code === 'FIELDS_TOO_FAST' || code === 'LEAD_TOO_FAST') {
        showFormError(say('errTooFast', 'Please take a moment before submitting.'));
        return;
      }
      if (code === 'LEAD_REJECTED' || code === 'REGISTRATION_REJECTED' || code === 'FIELDS_REJECTED') {
        // Was the "finishing the security check" line, which reads as "wait"
        // and was the second half of what QA saw: a message that asks for
        // patience while nothing is happening. With the token now refreshed
        // above, the honest instruction is to try again.
        showFormError(say('errRejected', 'We could not verify that submission. Please try again.'));
        return;
      }
      // Everything else — TRY_AGAIN, SHOPIFY_REJECTED, NETWORK_ERROR,
      // RATE_LIMITED, TAG_NOT_ALLOWED, a rejected key — reads the same to an
      // applicant: it did not send, try again. The code goes to the console so
      // a developer can tell them apart.
      console.warn('[pruvit-apply] submission failed:', error);
      showFormError(say('errGeneric', 'Something went wrong sending your application. Please try again.'));
    };

    const sdk = await window.pruvitMVQore;
    if (!sdk) {
      // No SDK, no transport. Say so rather than leaving a button that looks
      // live and silently does nothing.
      console.warn('[pruvit-apply] MV Qore SDK unavailable; the form cannot submit');
      showFormError(say('errGeneric', 'Something went wrong sending your application. Please try again.'));
      return;
    }

    const status = await sdk.getStatus();
    if (!status.installed || !status.configured) {
      console.warn('[pruvit-apply] MV Qore is not installed or not configured on this store');
      return;
    }

    // ---- referrer ----------------------------------------------------------
    // The shared step (pruvit-referrer-step.js). Before the captcha, which can
    // take a moment to load, so the question is live as soon as the SDK is.
    if (mode === 'register' && window.pruvitReferrerStep) {
      step = await window.pruvitReferrerStep.mount(root, sdk);
    }

    // ---- captcha -----------------------------------------------------------
    // Mounted only when the store says bot protection is on. getBotProtection
    // fails closed to disabled, so a store that cannot answer renders the form
    // without a widget rather than not at all.
    const bot = await sdk.getBotProtection();
    botEnabled = !!bot.enabled;
    if (bot.enabled && bot.siteKey && captchaBox) {
      captchaBox.hidden = false;
      captchaBox.classList.add('frc-captcha');
      captchaBox.setAttribute('data-sitekey', bot.siteKey);
      captchaBox.setAttribute('data-start', 'auto');
      const ok = await loadCaptcha();
      if (!ok) {
        // Bot protection is on and the widget did not arrive, so the server
        // will refuse every submission. Better to say so than to let somebody
        // fill in twelve fields and be told "could not verify".
        showFormError(say('captchaFailed', 'Could not load the security check. Please reload the page.'));
      }
      // v2 auto-attaches to .frc-captcha elements present when the module
      // runs; this one was added first, but attach() again is harmless and
      // covers the case where the script was already loaded by another form.
      if (ok && window.frcaptcha && typeof window.frcaptcha.attach === 'function') {
        try {
          window.frcaptcha.attach();
        } catch (e) {
          console.warn('[pruvit-apply] Friendly Captcha did not attach:', e);
        }
      }
    }

    // ---- hand over ---------------------------------------------------------
    if (mode === 'fields') {
      // Signed in. Only the mvqore_form.* inputs travel; the server resolves
      // the customer from Shopify's signed proxy request.
      sdk.attachCustomerFieldsForm(form, { tags: [SUBMIT_TAG], onSuccess, onError });
    } else {
      sdk.attachRegistrationForm(form, {
        tags: [SUBMIT_TAG],
        source: 'pruvit-professional-application',
        // True since 2026-10-05. It was false on the theory that an
        // application has no referrer, and that is how QA created an account
        // with no parentRefcode. The step above now asks, the gate refuses to
        // submit without an answer, and this has the SDK refuse as well. Where
        // the merchant turns Referrer Bypass on, "I don't have one" stores
        // their default code as the active referrer, so it passes.
        requireReferrer: true,
        // Registration signs the new customer in and navigates by default,
        // which would carry them away from the page before the "application
        // received" state could be read. They are not being sent anywhere yet;
        // approval comes first.
        autoLogin: false,
        onSuccess,
        onError
      });
    }
  };

  roots.forEach((root) => {
    setup(root).catch((e) => console.warn('[pruvit-apply] setup failed:', e));
  });
})();
